import { db } from "../../../../../firebaseConfig";
import { auth } from "../../../../../firebaseConfig";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  serverTimestamp,
  getCountFromServer,
  query,
  where,
  getDocFromServer,
  getDocFromCache,
} from "firebase/firestore";
import { retryOperation } from "./utils.js";
import { isLowQuotaMode } from "../../../../../firebase/firestoreFetchPolicy";
import { ensureFreshToken } from "../../../../../firebase/authUtils";

/**
 * Get user data with accurate post count
 * @param {string} uid - User ID
 * @param {Object} options - Options including forceCount
 * @returns {Promise<Object|null>} - User data with post count
 */
export const getUserDataWithPostCount = async (uid, options = {}) => {
  const { forceCount = false } = options;
  if (!uid) {
    console.error(
      "getUserDataWithPostCount: No user ID provided. Returning null.",
    );
    return null;
  }

  try {
    await ensureFreshToken();

    const userRef = doc(db, "users", uid);

    const forceNetwork =
      process.env.REACT_APP_FORCE_SERVER_USER_READS === "true";
    const envPolicy = process.env.REACT_APP_USER_FETCH_POLICY;
    const lowQuota = isLowQuotaMode();
    const fetchPolicy =
      envPolicy || (forceNetwork ? "network-only" : "cache-first");

    let userSnap;
    if (fetchPolicy === "network-only") {
      try {
        userSnap = await (typeof getDocFromServer === "function"
          ? getDocFromServer(userRef)
          : getDoc(userRef));
      } catch (e) {
        userSnap = await getDoc(userRef);
      }
    } else {
      try {
        const cacheSnap = await (typeof getDocFromCache === "function"
          ? getDocFromCache(userRef)
          : getDoc(userRef));
        if (cacheSnap?.exists()) {
          userSnap = cacheSnap;
        } else if (lowQuota) {
          userSnap = cacheSnap;
        } else {
          try {
            userSnap = await (typeof getDocFromServer === "function"
              ? getDocFromServer(userRef)
              : getDoc(userRef));
          } catch (e) {
            userSnap = await getDoc(userRef);
          }
        }
      } catch (e) {
        userSnap = await getDoc(userRef);
      }
    }

    if (userSnap.exists()) {
      const userData = userSnap.data();

      const disableCounts =
        process.env.REACT_APP_DISABLE_USER_POST_COUNTS === "true" &&
        !forceCount;
      if (uid && !disableCounts) {
        const postsRef = collection(db, "posts");
        const q = query(
          postsRef,
          where("userId", "==", uid),
          where("status", "==", "active"),
        );
        console.log(
          "getUserDataWithPostCount: Counting ACTIVE posts for userId:",
          uid,
        );
        const postCountSnapshot = await retryOperation(() =>
          getCountFromServer(q),
        );
        const postCount = postCountSnapshot.data().count;
        console.log("getUserDataWithPostCount: Post count result:", {
          uid,
          postCount,
          userData,
        });
        return { id: userSnap.id, ...userData, postCount };
      }

      return {
        id: userSnap.id,
        ...userData,
        postCount: 0,
      };
    }

    console.log("No user document found for ID:", uid);
    return null;
  } catch (error) {
    console.error("Error in getUserDataWithPostCount:", error);
    throw error;
  }
};

/**
 * Update user data
 * @param {string} userId - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} - Updated user data
 */
export const updateUserData = async (userId, userData) => {
  return retryOperation(async () => {
    const userRef = doc(db, "users", userId);

    console.log("🔍 [updateUserData] Input:", {
      userId,
      userProfession: userData?.userProfession,
      profession: userData?.profession,
      name: userData?.name,
      allKeys: Object.keys(userData || {}),
    });

    const userSnap = await getDoc(userRef);
    const exists = userSnap.exists();

    const updateData = {
      ...userData,
      photoURL: userData.photoURL ?? userData.userPhotoURL ?? undefined,
      userPhotoURL: userData.userPhotoURL ?? userData.photoURL ?? undefined,
      userProfession:
        userData.userProfession ?? userData.profession ?? undefined,
      profession: userData.profession ?? userData.userProfession ?? undefined,
      displayName: userData.displayName ?? userData.name ?? undefined,
      name: userData.name ?? userData.displayName ?? undefined,
      username:
        userData.username ??
        userData.userName ??
        userData.displayName ??
        userData.name ??
        undefined,
      coverPhotoURL: userData.coverPhotoURL ?? undefined,
      skills: Array.isArray(userData.skills) ? userData.skills : [],
      city: userData.city ?? undefined,
      country: userData.country ?? undefined,
      countryCode: userData.countryCode ?? undefined,
      areaCode: userData.areaCode ?? undefined,
      locationPublic: userData.locationPublic ?? false,
      updatedAt: serverTimestamp(),
      lastUpdated: new Date().toISOString(),
    };

    if (userData.city && String(userData.city).trim()) {
      updateData.location = userData.country
        ? `${userData.city}, ${userData.country}`
        : userData.city;
    } else if (userData.location !== undefined) {
      updateData.location = userData.location;
    }

    if (!exists) {
      updateData.createdAt = serverTimestamp();
      updateData.uid = userId;
      updateData.email = userData.email || auth.currentUser?.email || "";
      if (updateData.postCount === undefined) updateData.postCount = 0;
      if (updateData.followingCount === undefined)
        updateData.followingCount = 0;
      if (updateData.followersCount === undefined)
        updateData.followersCount = 0;
      if (!updateData.following) updateData.following = [];
      if (!updateData.followers) updateData.followers = [];
    }

    Object.keys(updateData).forEach(
      (key) => updateData[key] === undefined && delete updateData[key],
    );

    console.log("🔍 [updateUserData] Writing to Firestore:", {
      city: updateData.city,
      country: updateData.country,
      location: updateData.location,
      userProfession: updateData.userProfession,
      profession: updateData.profession,
      name: updateData.name,
      displayName: updateData.displayName,
      photoURL: updateData.photoURL,
      userPhotoURL: updateData.userPhotoURL,
      hasPhotoURL: !!updateData.photoURL,
      inputPhotoURL: userData.photoURL,
    });

    await setDoc(userRef, updateData, { merge: true });

    const updatedDoc = await getDoc(userRef);
    const result = { id: updatedDoc.id, ...updatedDoc.data() };

    console.log("🔍 [updateUserData] Saved result:", {
      userProfession: result.userProfession,
      profession: result.profession,
    });

    return result;
  });
};

/**
 * Update existing users (batch operation)
 */
export const updateExistingUsers = async () => {
  try {
    console.log("Starting update process...");
    const usersRef = collection(db, "users");
    const snapshot = await getDocs(usersRef);

    console.log(`Found ${snapshot.docs.length} users to process`);

    const batchSize = 5;
    for (let i = 0; i < snapshot.docs.length; i += batchSize) {
      const batch = snapshot.docs.slice(i, i + batchSize);

      const batchPromises = batch.map(async (doc) => {
        try {
          const userData = doc.data();
          if (process.env.NODE_ENV !== "production") {
            console.log("Processing user document:", doc.id);
          }

          const nameToUse = userData.username || userData.name;
          if (nameToUse) {
            await updateDoc(doc.ref, {
              username_lower: nameToUse.toLowerCase(),
            });
            if (process.env.NODE_ENV !== "production") {
              console.log(`Updated user: ${nameToUse}`);
            }
          }
        } catch (error) {
          console.error(`Error updating user ${doc.id}:`, error);
        }
      });

      await Promise.all(batchPromises);

      if (i + batchSize < snapshot.docs.length) {
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
    }

    console.log("Finished updating all users");
    alert("Successfully updated all users!");
  } catch (error) {
    console.error("Error updating users:", error);
    alert("Error updating users: " + error.message);
  }
};
